function initImageSwiper(swiperRoot) {
  const track = swiperRoot.querySelector('.image-swiper__track');
  const prevButton = swiperRoot.querySelector('.image-swiper__arrow--prev');
  const nextButton = swiperRoot.querySelector('.image-swiper__arrow--next');

  if (!track || !prevButton || !nextButton) {
    return;
  }

  if (track.dataset.swiperReady === 'true') {
    return;
  }

  track.dataset.swiperReady = 'true';

  const slides = Array.from(track.querySelectorAll('.image-swiper__item'));

  if (!slides.length) {
    return;
  }

  let isDragging = false;
  let isAnimating = false;
  let startX = 0;
  let startScrollLeft = 0;
  let lastX = 0;
  let lastTime = 0;
  let velocity = 0;
  let animationFrame = 0;
  let hasMoved = false;
  let activePointerId = null;
  let startedOnLink = false;
  let suppressLinkClick = false;

  const DRAG_THRESHOLD = 8;

  const isLinkInteraction = (event) => Boolean(event.target.closest('.image-swiper__image-link'));

  const beginDragInteraction = (pointerId) => {
    setTrackInteractionState(true);

    if (track.setPointerCapture) {
      track.setPointerCapture(pointerId);
    }
  };

  // Clone slides for infinite loop via fragments so prepend keeps source order.
  const prependFragment = document.createDocumentFragment();
  const appendFragment = document.createDocumentFragment();

  const cloneSlide = (slide) => {
    const clone = slide.cloneNode(true);
    clone.classList.add('is-clone');
    clone.removeAttribute('id');
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    return clone;
  };

  slides.forEach((slide) => {
    prependFragment.appendChild(cloneSlide(slide));
    appendFragment.appendChild(cloneSlide(slide));
  });

  track.insertBefore(prependFragment, track.firstChild);
  track.appendChild(appendFragment);

  const getLayoutMetrics = () => {
    const firstSlide = track.querySelector('.image-swiper__item');

    if (!firstSlide) {
      return { itemWidth: 0, loopWidth: 0 };
    }

    const slideWidth = firstSlide.offsetWidth;
    const gap = parseInt(window.getComputedStyle(track).gap || 0, 10);

    return {
      itemWidth: slideWidth + gap,
      loopWidth: (slideWidth + gap) * slides.length,
    };
  };

  const stopAnimation = () => {
    cancelAnimationFrame(animationFrame);
    isAnimating = false;
  };

  const setTrackInteractionState = (isActive) => {
    track.style.scrollBehavior = 'auto';
    track.style.setProperty('scroll-snap-type', isActive ? 'none' : '', 'important');
    track.classList.toggle('is-dragging', isActive);
  };

  const normalizeScroll = () => {
    const { loopWidth } = getLayoutMetrics();

    if (!loopWidth) {
      return;
    }

    if (track.scrollLeft >= loopWidth * 2) {
      track.scrollLeft -= loopWidth;
    } else if (track.scrollLeft < loopWidth) {
      track.scrollLeft += loopWidth;
    }
  };

  const animateScrollTo = (target, duration = 280, onComplete) => {
    stopAnimation();

    const start = track.scrollLeft;
    const distance = target - start;

    if (Math.abs(distance) < 1) {
      onComplete?.();
      return;
    }

    isAnimating = true;
    setTrackInteractionState(true);
    const startTime = performance.now();

    const step = (now) => {
      const progress = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);

      track.scrollLeft = start + distance * eased;

      if (progress < 1) {
        animationFrame = requestAnimationFrame(step);
        return;
      }

      isAnimating = false;
      setTrackInteractionState(false);
      normalizeScroll();
      onComplete?.();
    };

    animationFrame = requestAnimationFrame(step);
  };

  const snapToNearest = () => {
    normalizeScroll();

    const { itemWidth } = getLayoutMetrics();

    if (!itemWidth) {
      return;
    }

    const target = Math.round(track.scrollLeft / itemWidth) * itemWidth;

    animateScrollTo(target);
  };

  const applyMomentum = () => {
    const { itemWidth } = getLayoutMetrics();

    if (!itemWidth) {
      snapToNearest();
      return;
    }

    let momentum = velocity * 16;
    const maxMomentum = itemWidth * 1.5;

    momentum = Math.max(-maxMomentum, Math.min(maxMomentum, momentum));

    if (Math.abs(momentum) < 8) {
      snapToNearest();
      return;
    }

    const target = Math.round((track.scrollLeft - momentum) / itemWidth) * itemWidth;

    animateScrollTo(target);
  };

  track.scrollLeft = getLayoutMetrics().loopWidth;

  const handleArrowClick = (direction) => {
    if (isAnimating) {
      return;
    }

    stopAnimation();
    normalizeScroll();

    const { itemWidth } = getLayoutMetrics();
    const targetScroll = direction === 'next'
      ? track.scrollLeft + itemWidth
      : track.scrollLeft - itemWidth;

    animateScrollTo(targetScroll);
  };

  nextButton.addEventListener('click', () => handleArrowClick('next'));
  prevButton.addEventListener('click', () => handleArrowClick('prev'));

  const onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    if (isAnimating) {
      stopAnimation();
      setTrackInteractionState(false);
    }

    startedOnLink = isLinkInteraction(event);
    isDragging = true;
    hasMoved = false;
    suppressLinkClick = false;
    activePointerId = event.pointerId;
    startX = event.clientX;
    lastX = event.clientX;
    lastTime = performance.now();
    startScrollLeft = track.scrollLeft;
    velocity = 0;

    if (!startedOnLink) {
      beginDragInteraction(event.pointerId);
    }
  };

  const onPointerMove = (event) => {
    if (!isDragging || event.pointerId !== activePointerId) {
      return;
    }

    const dx = event.clientX - startX;

    if (!hasMoved && Math.abs(dx) > DRAG_THRESHOLD) {
      hasMoved = true;
      suppressLinkClick = true;
      beginDragInteraction(event.pointerId);
    }

    if (!hasMoved) {
      return;
    }

    event.preventDefault();

    const now = performance.now();
    const deltaTime = now - lastTime;

    if (deltaTime > 0) {
      velocity = (event.clientX - lastX) / deltaTime;
    }

    lastX = event.clientX;
    lastTime = now;
    track.scrollLeft = startScrollLeft - dx;
  };

  const onPointerUp = (event) => {
    if (!isDragging || event.pointerId !== activePointerId) {
      return;
    }

    isDragging = false;
    activePointerId = null;
    setTrackInteractionState(false);

    if (track.releasePointerCapture && track.hasPointerCapture(event.pointerId)) {
      track.releasePointerCapture(event.pointerId);
    }

    if (hasMoved) {
      event.preventDefault();
      suppressLinkClick = true;
      applyMomentum();
      startedOnLink = false;
      return;
    }

    normalizeScroll();
    startedOnLink = false;
  };

  track.addEventListener('click', (event) => {
    if (!event.target.closest('.image-swiper__image-link') || !suppressLinkClick) {
      return;
    }

    event.preventDefault();
  });

  track.addEventListener('pointerdown', onPointerDown);
  track.addEventListener('pointermove', onPointerMove);
  track.addEventListener('pointerup', onPointerUp);
  track.addEventListener('pointercancel', onPointerUp);
  track.addEventListener('dragstart', (event) => event.preventDefault());
  track.addEventListener('scroll', () => {
    if (!isDragging && !isAnimating) {
      normalizeScroll();
    }
  }, { passive: true });
}

function initAllImageSwipers() {
  document.querySelectorAll('.image-swiper').forEach((swiperRoot) => {
    initImageSwiper(swiperRoot);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllImageSwipers);
} else {
  initAllImageSwipers();
}
